import { useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { BasicButtons, CustomButtons, FreeButtons, MogulButtons, ProButtons } from 'components/Pricing/PaymentButton';
import { store } from 'components/Store';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import returnCorrectWPUrl from 'components/Dashboard/utilities/returnCorrectWPUrl';

const DataHubPlanButtons = ({ paymentButtonProps }) => {
  const { setPlanToCheckout, showPopUp, bannerConditions, conditionForUpgrade } = paymentButtonProps ?? {};
  const { isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const globalState = useContext(store);
  const { state } = globalState;
  const { activeSubscriptions, team } = state;
  const { myself } = team ?? {};
  const { planName: mainPlanName, subscriptionStatus } = activeSubscriptions ?? {};
  let planName = mainPlanName === 'Campaigner' ? 'Starter' : mainPlanName;
  const plan = 'DataHub';
  const role = myself !== undefined ? myself.role : '';

  const canCheckout = role === 'Owner' && !bannerConditions && !conditionForUpgrade;
  const isCurrentPlan = planName === 'Data Hub';
  const blockConditional =
    (isCurrentPlan &&
      (subscriptionStatus === 'InTrial' ||
        subscriptionStatus === 'TrialEnded' ||
        subscriptionStatus === 'SubscriptionEnded') &&
      role !== 'Owner') ||
    (role !== 'Owner' && !isCurrentPlan);
  const showPadlock = blockConditional || bannerConditions || (isCurrentPlan && conditionForUpgrade);

  const buyLabel =
    (subscriptionStatus === 'Subscribed' ||
      subscriptionStatus === 'OnFreePlan' ||
      subscriptionStatus === 'OnFreePlanOverLimit' ||
      subscriptionStatus === 'SubscribedOverLimit') &&
    isCurrentPlan &&
    !conditionForUpgrade
      ? 'Your plan'
      : 'Buy now';

  return (
    <div className='usa-pricing-buttons specific-x-padding-32 specific-bottom-margin-28 d-flex gap-10 align-items-center justify-content-center'>
      {!isAuthenticated && (
        <button
          type='button'
          className='general-button action-button payment-button-primary rounded-pill px-4 py-2'
          onClick={() => navigate('/data-hub')}
        >
          Try it free
        </button>
      )}
      {isAuthenticated && (
        <button
          type='button'
          className='general-button rounded-pill payment-button px-4 py-2'
          data-tooltip-place='top'
          data-tooltip-html={blockConditional ? 'Contact your account owner' : ''}
          data-tooltip-id='pricing-page-tooltip'
          disabled={blockConditional}
          onClick={() => {
            if (canCheckout && buyLabel !== 'Your plan') {
              setPlanToCheckout(plan);
              showPopUp();
            }
          }}
          style={{ cursor: canCheckout && buyLabel !== 'Your plan' ? 'pointer' : 'auto' }}
        >
          {buyLabel}
          {showPadlock && <span className='pricing-padlock icon-padlock paragraph-p2 specific-left-margin-8' />}
        </button>
      )}
    </div>
  );
};

const SeeAllFeaturesButton = ({ goToElement }) => {
  return (
    <div className='specific-x-padding-20 specific-bottom-padding-24'>
      <button
        className='general-button border-general border-light-blue rounded-pill px-3 specific-y-padding-4 w-100 bg-main-white text-light-blue hover-light-blue hover-text-white border-hover-white'
        onClick={goToElement}
      >
        See all features
      </button>
    </div>
  );
};

const PricingCardWrapper = ({ children, className, goToElement, noSeeAllFeatures = false }) => {
  return (
    <div className={`pricing-card ${className}`}>
      <div className='d-flex flex-column justify-content-between h-100'>
        <div>{children}</div>
        {!noSeeAllFeatures && <SeeAllFeaturesButton goToElement={goToElement} />}
      </div>
    </div>
  );
};

const ViewFetures = ({ goToElement }) => {
  return (
    <p className='view-features' onClick={goToElement}>
      Compare plans
    </p>
  );
};

const FreePlan = ({ paymentButtonProps, goToElement }) => {
  return (
    <PricingCardWrapper goToElement={goToElement}>
      <div className='pricing-card--title'>
        <h2 className='py-3'>Free</h2>
        <div className='price-container pb-4'>
          <p className='pricing-card--price'>
            £0 <span className='month-text'>/ month</span>
          </p>
        </div>
      </div>
      <FreeButtons paymentButtonProps={paymentButtonProps} />
      <ViewFetures goToElement={goToElement} />
      <div className='pricing-features-list pt-3'>
        <ul className='policy-list px-4 pb-5 text-left pricing-features-container'>
          <li>monitoring portal</li>
          <li>1 topic</li>
          <li>5 keywords</li>
        </ul>
      </div>
    </PricingCardWrapper>
  );
};

const BasicPlan = ({ period, price, paymentButtonProps, goToElement }) => {
  return (
    <PricingCardWrapper goToElement={goToElement}>
      <div className='pricing-card--title'>
        <h2 className='py-3'>Starter</h2>
        <div className='price-container pb-4'>
          <p className='pricing-card--price'>
            £{price} <span className='month-text'>/ month</span>
          </p>
          <p className='pricing-card--billed'>
            {period === 'annually' ? `£${thousandCommas(price * 12)} ` : ''}billed {period}
          </p>
        </div>
      </div>
      <BasicButtons paymentButtonProps={paymentButtonProps} />
      <ViewFetures goToElement={goToElement} />
      <div className='pricing-features-list pt-3'>
        <ul className='policy-list px-4 pb-5 text-left pricing-features-container'>
          <li>monitoring portal</li>
          <li>1 topic</li>
          <li>unlimited keywords</li>
          <li>political CRM</li>
          <li>stakeholder mapping</li>
          <li>lobbying platform</li>
          <li>AI assistant</li>
        </ul>
      </div>
    </PricingCardWrapper>
  );
};

const ProPlan = ({ period, price, paymentButtonProps, goToElement }) => {
  return (
    <PricingCardWrapper goToElement={goToElement}>
      <div className='pricing-card--title'>
        <h2 className='py-3'>Pro</h2>
        <div className='price-container pb-4'>
          <p className='pricing-card--price'>
            £{price} <span className='month-text'>/ month</span>
          </p>
          <p className='pricing-card--billed'>
            {period === 'annually' ? `£${thousandCommas(price * 12)} ` : ''}billed {period}
          </p>
        </div>
      </div>
      <ProButtons paymentButtonProps={paymentButtonProps} />
      <ViewFetures goToElement={goToElement} />
      <div className='pricing-features-list pt-3'>
        <ul className='policy-list px-4 pb-5 text-left pricing-features-container'>
          <li>monitoring portal</li>
          <li>3 topics *</li>
          <li>unlimited keywords</li>
          <li>political CRM</li>
          <li>stakeholder mapping</li>
          <li>lobbying platform</li>
          <li>AI assistant</li>
          <li>AI summaries</li>
          <li>analytics</li>
        </ul>
      </div>
    </PricingCardWrapper>
  );
};

const MogulPlan = ({ period, price, agencyPrice, paymentButtonProps, goToElement }) => {
  const { activeTab } = paymentButtonProps || {};
  let currentPrice = activeTab === 'agencies' ? agencyPrice : price;
  return (
    <PricingCardWrapper goToElement={goToElement}>
      <div className='pricing-card--title mogul-title'>
        <h2 className='py-3'>Mogul</h2>
        <div className='price-container pb-4'>
          <p className='pricing-card--price '>
            <span className='from-text'></span> £{currentPrice}{' '}
            <span className='month-text'>{activeTab === 'agencies' ? '/ client ' : ''}/ month</span>
          </p>
          <p className='pricing-card--billed'>
            {period === 'annually' ? `£${thousandCommas(currentPrice * 12)} ` : ''} billed {period}
          </p>
        </div>
      </div>
      <MogulButtons paymentButtonProps={paymentButtonProps} />
      <ViewFetures goToElement={goToElement} />
      <div className='pricing-features-list pt-3'>
        <ul className='policy-list px-4 pb-5 text-left pricing-features-container'>
          <li>monitoring portal</li>
          {activeTab !== 'agencies' && <li>5 topics *</li>}
          {activeTab !== 'agencies' && <li>unlimited keywords</li>}
          <li>political CRM</li>
          <li>stakeholder mapping</li>
          <li>lobbying platform</li>
          <li>AI assistant</li>
          <li>AI summaries</li>
          {activeTab !== 'agencies' && <li>analytics</li>}
          <li>Parliament live</li>
          <li>dynamic groups</li>
          {activeTab !== 'agencies' && <li>reports</li>}
        </ul>
      </div>
    </PricingCardWrapper>
  );
};

const CustomPlan = ({ paymentButtonProps, planName, showTweets, goToElement }) => {
  const { activeTab } = paymentButtonProps || {};
  return (
    <PricingCardWrapper className='enterprise-card' goToElement={goToElement}>
      <div className='pricing-card--title'>
        <h2 className='py-3'>Enterprise</h2>
        <div className='price-container pb-4'>
          {activeTab === 'agencies' ? (
            <p className='pricing-card--price '>
              <span className='from-text'></span> £395{' '}
              <span className='month-text'>/ client / month{activeTab === 'agencies' ? '*' : ''}</span>
            </p>
          ) : (
            <p className='pricing-card--price contact-us-price'>
              <span className='from-text'></span> Contact us <span className='month-text'></span>
            </p>
          )}
          <p className='pricing-card--billed'>{activeTab === 'agencies' ? `£4,740` : ''} billed annually</p>
        </div>
      </div>
      <CustomButtons paymentButtonProps={paymentButtonProps} planName={planName} />
      <ViewFetures goToElement={goToElement} />
      <div className='pricing-features-list pt-3'>
        <ul className='policy-list px-4 pb-5 text-left pricing-features-container'>
          <li>monitoring portal</li>
          {activeTab !== 'agencies' && <li>unlimited topics</li>}
          {activeTab !== 'agencies' && <li>unlimited keywords</li>}
          <li>political CRM</li>
          <li>stakeholder mapping</li>
          <li>lobbying platform</li>
          <li>AI assistant</li>
          <li>AI summaries</li>
          <li>analytics</li>
          <li>Parliament live</li>
          <li>dynamic groups</li>
          {activeTab !== 'agencies' && <li>reports</li>}
          {showTweets && <li>policymakers on X</li>}
          <li>early access to news **</li>
          <li>research team</li>
        </ul>
      </div>
    </PricingCardWrapper>
  );
};

const DataHubPlan = ({ price, paymentButtonProps, goToElement }) => {
  return (
    <PricingCardWrapper goToElement={goToElement} className='pricing-card-usa' noSeeAllFeatures={true}>
      <div className='pricing-card--title'>
        <h2 className='py-3 title-h2 mb-0'>Data Hub</h2>
        <div className='price-container specific-bottom-margin-28'>
          <p className='pricing-card--price '>
            <span className='from-text'></span> ${price} <span className='month-text'>/ user / month</span>
          </p>
        </div>
      </div>
      <div className='pricing-description specific-x-padding-32 specific-bottom-margin-28'>
        <p className='text-center title-h4'>
          For organizations who already use Politico Pro or other political monitoring and CRM services
        </p>
      </div>
      <DataHubPlanButtons paymentButtonProps={paymentButtonProps} />
      <div>
        <ul className='policy-list specific-x-padding-32 pb-5 text-left'>
          <li className='specific-bottom-margin-12 d-flex align-items-center'>
            <span className='icon-circle-check mr-3 title-h3'></span>Political data on any issue
          </li>
          <li className='specific-bottom-margin-12 d-flex align-items-center'>
            <span className='icon-circle-check mr-3 title-h3'></span>Congressional Record and X posts
          </li>
          <li className='specific-bottom-margin-12 d-flex align-items-center'>
            <span className='icon-circle-check mr-3 title-h3'></span>Stakeholder sentiment analysis
          </li>
          <li className='specific-bottom-margin-12 d-flex align-items-center'>
            <span className='icon-circle-check mr-3 title-h3'></span>Filter by Party, House, State and more
          </li>
          <li className='specific-bottom-margin-12 d-flex align-items-center'>
            <span className='icon-circle-check mr-3 title-h3'></span>Export or embed real-time charts
          </li>
        </ul>
      </div>
    </PricingCardWrapper>
  );
};

const PublicAffairsSuitePlan = ({ goToElement }) => {
  return (
    <PricingCardWrapper goToElement={goToElement} className='pricing-card-usa' noSeeAllFeatures={true}>
      <div className='pricing-card--title'>
        <h2 className='py-3 title-h2 mb-0'>Public Affairs Suite</h2>
        <div className='price-container specific-bottom-margin-28'>
          <p className='pricing-card--price '>
            <span>Contact us</span>
          </p>
        </div>
      </div>
      <div className='pricing-description specific-x-padding-32 specific-bottom-margin-28'>
        <p className='text-center title-h4'>
          For organizations looking for a full suite of political monitoring, data and public affairs tools
        </p>
      </div>
      <div className='specific-x-padding-32 specific-bottom-margin-28 d-flex justify-content-center'>
        <button
          type='button'
          className='general-button action-button rounded-pill payment-button-primary px-4 py-2'
          onClick={() => {
            window.location.assign(`${returnCorrectWPUrl()}/demo`);
          }}
        >
          Book a demo
        </button>
      </div>
      <div>
        <ul className='policy-list specific-x-padding-32 pb-5 text-left'>
          <li className='specific-bottom-margin-12 d-flex align-items-center'>
            <span className='icon-circle-check mr-3 title-h3'></span>Data Hub
          </li>
          <li className='specific-bottom-margin-12 d-flex align-items-center'>
            <span className='icon-circle-check mr-3 title-h3'></span>Monitoring portal
          </li>
          <li className='specific-bottom-margin-12 d-flex align-items-center'>
            <span className='icon-circle-check mr-3 title-h3'></span>Stakeholder mapping
          </li>
          <li className='specific-bottom-margin-12 d-flex align-items-center'>
            <span className='icon-circle-check mr-3 title-h3'></span>Policymaker and staffer CRM
          </li>
          <li className='specific-bottom-margin-12 d-flex align-items-center'>
            <span className='icon-circle-check mr-3 title-h3'></span>Report-builder
          </li>
        </ul>
      </div>
    </PricingCardWrapper>
  );
};

export { BasicPlan, ProPlan, MogulPlan, CustomPlan, FreePlan, DataHubPlan, PublicAffairsSuitePlan };
